'use client';

import { Box, Typography } from '@wishket/design-system';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import { RefObject, useEffect, useRef, useState } from 'react';

import { useDeviceCheck } from '@/shared/hooks';

interface GrassTooltipProps {
  score: number;
  date: string;
}

interface TooltipBodyProps {
  boxRef: RefObject<HTMLDivElement | null>;
  text: string;
  handleClose: () => void;
}

const TOOLTIP_HEIGHT = 33;
const TILE_SIZE = 10;

const GrassTooltip = ({ score, date }: GrassTooltipProps) => {
  const { isDesktop } = useDeviceCheck();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const scoreText = `${isDesktop ? dayjs(date).format('M월 D일 ') : ''}${score}개 활동`;

  const handleOpenTooltip = () => setIsTooltipOpen(true);
  const handleCloseTooltip = () => setIsTooltipOpen(false);

  return (
    <>
      <Box
        ref={boxRef}
        className="size-full"
        onMouseEnter={handleOpenTooltip}
        onMouseLeave={handleCloseTooltip}
      />
      {isTooltipOpen && (
        <TooltipBody
          boxRef={boxRef}
          text={scoreText}
          handleClose={handleCloseTooltip}
        />
      )}
    </>
  );
};

const TooltipBody = ({ boxRef, text, handleClose }: TooltipBodyProps) => {
  useEffect(() => {
    window.addEventListener('scroll', handleClose);
    const grassContainer = document.getElementById('grass-body-container');
    if (grassContainer) {
      grassContainer.addEventListener('scroll', handleClose);
    }

    return () => {
      window.removeEventListener('scroll', handleClose);
      if (grassContainer) {
        grassContainer.removeEventListener('scroll', handleClose);
      }
    };
  }, [handleClose]);

  const rect = boxRef.current?.getBoundingClientRect();
  const top = `${(rect?.top || 0) - TOOLTIP_HEIGHT + TILE_SIZE / 2}px`;
  const left = `${(rect?.left || 0) + TILE_SIZE / 2}px`;

  return (
    <Box
      className={twMerge(
        'pointer-events-none fixed z-10 select-none',
        '-translate-x-1/2'
      )}
      style={{ top, left }}
    >
      <Arrow />
      <Box className="z-0 flex h-auto min-h-[28px] w-full items-center justify-center rounded-lg bg-w-black px-3 py-1 text-center opacity-80">
        <Typography
          variant="body3"
          className="whitespace-pre font-medium text-w-white opacity-100"
        >
          {text}
        </Typography>
      </Box>
    </Box>
  );
};

const Arrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="5"
    height="10"
    viewBox="0 0 5 10"
    fill="none"
    className={twMerge(
      'absolute z-10 opacity-80',
      '-bottom-[7px] left-1/2 -translate-x-1/2 -rotate-90'
    )}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.96244e-08 5L5 -1.10263e-07L5 10L5.96244e-08 5Z"
      fill="black"
    />
  </svg>
);

export default GrassTooltip;
